import { logger } from "@scott/logger";
import {
  AccordionAdapter,
  type AccordionInput,
} from "@/services/contentful/components/Accordion/accordion.adapter";
import {
  ContentSliderAdapter,
  type ContentSliderInput,
} from "@/services/contentful/components/ContentSlider/content-slider.adapter";
import {
  GeometryTableAdapter,
  type GeometryTableInput,
} from "@/services/contentful/components/GeometryTable/geometry-table.adapter";
import {
  SectionMediaAdapter,
  type SectionMediaInput,
} from "@/services/contentful/components/MediaSection/section-media.adapter";
import {
  ProductListingAdapter,
  type ProductListingInput,
} from "@/services/contentful/components/ProductListing/product-listing.adapter";
import {
  SectionClaimAdapter,
  type SectionClaimInput,
} from "@/services/contentful/components/SectionClaim/section-claim.adapter";
import {
  type RawSectionCounterBlock,
  SectionCounterAdapter,
} from "@/services/contentful/components/SectionCounter/section-counter.adapter";
import {
  SectionGalleryAdapter,
  type SectionGalleryInput,
} from "@/services/contentful/components/SectionGallery/section-gallery.adapter";
import {
  SectionHeroAdapter,
  type SectionHeroInput,
} from "@/services/contentful/components/SectionHero/section-hero.adapter";
import {
  SectionHotspotAdapter,
  type SectionHotspotInput,
} from "@/services/contentful/components/SectionHotspot/section-hotspot.adapter";
import {
  SectionIntroTextAdapter,
  type SectionIntroTextInput,
} from "@/services/contentful/components/SectionIntroText/section-intro-text.adapter";
import {
  SectionQuoteAdapter,
  type SectionQuoteInput,
} from "@/services/contentful/components/SectionQuote/section-quote.adapter";
import {
  SectionSliderAdapter,
  type SectionSliderInput,
} from "@/services/contentful/components/SectionSlider/section-slider.adapter";
import {
  SectionTaglineAdapter,
  type SectionTaglineInput,
} from "@/services/contentful/components/SectionTagline/section-tagline.adapter";
import {
  SectionTeaserAdapter,
  type SectionTeaserInput,
} from "@/services/contentful/components/SectionTeaser/section-teaser.adapter";
import {
  SectionTextAdapter,
  type SectionTextInput,
} from "@/services/contentful/components/SectionText/section-text.adapter";
import {
  SectionTextImageAdapter,
  type SectionTextImageInput,
} from "@/services/contentful/components/SectionTextImage/section-text-image.adapter";
import {
  TabContainerAdapter,
  type TabContainerInput,
} from "@/services/contentful/components/TabContainer/tab-container.adapter";
import type {
  MinimalRawBlock,
  PageBlock,
  RawContentBlockBase,
  UnknownPageBlock,
} from "@/types/page-block";
import { getBlockId } from "./utils";

/**
 * Handles unknown or unsupported block types.
 */
function adaptUnknownBlock(item: MinimalRawBlock, index: number): PageBlock {
  return {
    id: getBlockId(item, index),
    __typename: item?.__typename ?? "unknown",
    internalName: item?.internalName ?? "Unknown Block",
    reason: "Unsupported content block type",
    raw: item,
  };
}

type RegisteredContentBlock = Exclude<PageBlock, UnknownPageBlock>;

type BlockAdapterFn = (
  item: RegisteredContentBlock,
  index: number,
  locale?: string,
  basePath?: string
) => PageBlock;

/**
 * Map Contentful Content-Type to Next.js Component
 */
const componentMapping: Record<string, BlockAdapterFn> = {
  SectionClaim: (item, index) =>
    SectionClaimAdapter(item as SectionClaimInput, index),
  SectionTagline: (item, index) =>
    SectionTaglineAdapter(item as SectionTaglineInput, index),
  SectionQuote: (item, index) =>
    SectionQuoteAdapter(item as SectionQuoteInput, index),
  SectionHero: (item, index, _locale, basePath) =>
    SectionHeroAdapter(item as SectionHeroInput, index, basePath),
  SectionIntroText: (item, index) =>
    SectionIntroTextAdapter(item as SectionIntroTextInput, index),
  SectionText: (item, index) =>
    SectionTextAdapter(item as SectionTextInput, index),
  SectionCounter: (item, index) =>
    SectionCounterAdapter(item as RawSectionCounterBlock, index),
  SectionTextImage: (item, index, _locale, basePath) =>
    SectionTextImageAdapter(item as SectionTextImageInput, index, basePath),
  SectionGallery: (item, index) =>
    SectionGalleryAdapter(item as SectionGalleryInput, index),
  SectionTeaser: (item, index, _locale, basePath) =>
    SectionTeaserAdapter(item as SectionTeaserInput, index, basePath),
  Hotspot: (item, index, locale) =>
    SectionHotspotAdapter(item as SectionHotspotInput, index, locale),
  SectionMedia: (item, index) =>
    SectionMediaAdapter(item as SectionMediaInput, index),
  ProductListing: (item, index) =>
    ProductListingAdapter(item as ProductListingInput, index),
  GeometryTable: (item, index) =>
    GeometryTableAdapter(item as GeometryTableInput, index),
  AccordionD2C: (item, index, locale, basePath) =>
    AccordionAdapter(item as AccordionInput, index, locale, basePath),
  ContentSliderD2C: (item, index, locale, basePath) =>
    ContentSliderAdapter(item as ContentSliderInput, index, locale, basePath),
  TabContainerD2C: (item, index, locale, basePath) =>
    TabContainerAdapter(item as TabContainerInput, index, locale, basePath),
  SectionSlider: (item, index, _locale, basePath) =>
    SectionSliderAdapter(item as SectionSliderInput, index, basePath),
};

/**
 * Main adapter function that converts raw Contentful blocks to PageBlocks.
 * Uses registry pattern for extensibility and type safety.
 *
 * @param basePath The active market's `/{country}/{lang}`, which internal page
 * links are placed below.
 */
export function contentBlocksAdapter(
  items: Array<RegisteredContentBlock | null> | null | undefined,
  locale?: string,
  basePath?: string
): PageBlock[] {
  if (!items) {
    return [];
  }

  return items
    .map((item, index) => {
      if (!item?.__typename) {
        return adaptUnknownBlock(item as RawContentBlockBase, index);
      }

      const adapter = componentMapping[item.__typename];
      try {
        return adapter
          ? adapter(item, index, locale, basePath)
          : adaptUnknownBlock(item, index);
      } catch (error) {
        logger.error(
          `contentBlocksAdapter: failed to adapt block "${item.__typename}" at index ${index}`,
          { error }
        );
        return null;
      }
    })
    .filter((block): block is PageBlock => block !== null);
}
